import { useTranslation } from 'react-i18next';
import { Stack, Text } from '@figma/fpl-components';
import { Icon24LogoWeavy } from '@figma/fpl-icons';
import cssVars from '@figma/fpl-tokens/css-vars';
import { color, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { I18N_KEYS } from '@/language/keys';
import { ToolMainViewHeader } from './ToolMainViewHeader';
import { ToolExampleOutputs } from './ToolExampleOutputs';
import { panelStyles } from './ToolLayout.stylex';
import type { ReactNode } from 'react';
import type { ToolViewMetadata } from '@/api/toolView.api';
import type { UploadedAsset } from '@/types/api/assets';

interface ToolMainViewProps {
  toolName: string;
  metadata?: ToolViewMetadata;
  isEmpty: boolean;
  examples: UploadedAsset[];
  children: ReactNode;
}

export function ToolMainView({ toolName, metadata, isEmpty, examples, children }: ToolMainViewProps) {
  const { t } = useTranslation();
  const labels = I18N_KEYS.TOOL_VIEW;
  const title = metadata?.name || toolName || t(labels.UNTITLED);

  return (
    <Stack.Col data-testid="tool-main-view" {...stylex.props(styles.mainView, panelStyles.mainView)}>
      <ToolMainViewHeader title={title} metadata={metadata} />
      <Stack.Col {...stylex.props(styles.body)}>
        {isEmpty ? (
          <Stack.Col {...stylex.props(styles.emptyBody, examples.length > 0 && styles.emptyBodyWithExamples)}>
            <Stack.Col
              data-testid="tool-empty-state"
              align="center"
              justify="center"
              gap={cssVars.spacer.x12px}
              {...stylex.props(styles.emptyState)}
            >
              <Icon24LogoWeavy />
              <Stack.Col align="center" gap={cssVars.spacer.x4px}>
                <Text.div variant="body-medium-strong" color="default" align="center">
                  {t(labels.EMPTY_TITLE)}
                </Text.div>
                <Text.div variant="body-medium" color="secondary" align="center">
                  {t(labels.EMPTY_SUBTITLE)}
                </Text.div>
              </Stack.Col>
            </Stack.Col>
          </Stack.Col>
        ) : (
          children
        )}
        <ToolExampleOutputs title={title} examples={examples} />
      </Stack.Col>
    </Stack.Col>
  );
}

const styles = stylex.create({
  mainView: {
    flex: 1,
    minHeight: 0,
    height: '100%',
    overflowY: 'auto',
  },
  body: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    // Let the loader size itself to the visible space below the variable-height header.
    containerType: 'size',
  },
  emptyBody: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    paddingInline: spacer.spacer32px,
    paddingBottom: spacer.spacer32px,
  },
  emptyBodyWithExamples: { paddingBottom: spacer.spacer16px },
  emptyState: {
    flex: 1,
    padding: 10,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: color.border,
    borderRadius: 5,
    color: color.text,
  },
});
